/** The turn, day, week and month boundaries.
 *
 * The classic's calendar is a single counter dressed three ways: seven days make a
 * week, four weeks a month. `TurnState` stores the dressed form because that is
 * what the player is shown; `absoluteDay` reconstructs the counter when
 * something needs to age.
 *
 * Income and the daily restores are applied at the *day* boundary, for every
 * player at once, rather than at the start of each player's own turn. The two
 * readings differ only in what a player sees in their treasury while an
 * opponent is still moving, and the day boundary is the one that makes "income
 * landed on day N" a fact about the state rather than about the turn order.
 */

import type { ArmyId, BuildingDefId, CreatureDefId, HeroId, PlayerId, TownId } from "../ids.js";
import type { BuildingDef } from "../content/registry.js";
import { expireModifiers } from "../modifiers.js";
import { FP_ONE, roundHalfAway } from "../numeric.js";
import type { GameState, PlayerState, ResourceKind, TownState } from "../state.js";
import { RESOURCES } from "../state.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import {
  DAYS_PER_WEEK, WEEKS_PER_MONTH, absoluteDay, addResource, closePending, dropHero,
  patchHero, patchPlayer, patchTown, patchTurn, rollBounded, rollChanceBp,
} from "./ctx.js";
import { dailyMovement, manaRegen, maxMana, statFor } from "./hero.js";
import {
  INCOME_STAT, PLAIN_WEEK, addBuilding, applyWeeklyGrowth, arriveInTown, buildIllegality,
  fillGuild, guildLevelOf, halveAvailable, refreshTavern, townIncome, type GrowthEvent,
} from "./town.js";
import { GRAIL_SLOT, parseVictoryCondition } from "./create.js";
import { grailBearerIn, spendGrail } from "./grail.js";
import { applyTimedEvents, collectMineIncome, weeklyObjectReset } from "./objects.js";
import { deliverCaravans } from "./caravan.js";
import { recomputeVisibility } from "./visibility.js";

/**
 * Special-week odds. The classic never published these; the values below are the ones
 * the community measures and the expansion's templates assume, and they are constants so
 * a scenario can override them without touching the roll.
 */
export const WEEK_OF_CREATURE_BP = 1000;
export const MONTH_OF_CREATURE_BP = 1500;
export const MONTH_OF_PLAGUE_BP = 500;
/** "Week of the ..." adds five of that creature to every dwelling. */
export const WEEK_CREATURE_BONUS = 5;
/** "Month of the ..." doubles that creature's growth. */
export const MONTH_CREATURE_MUL_FP = 2 * FP_ONE;

// ------------------------------------------------------------------ turn

/** The next player in the fixed order who is still in the game. */
export function nextPlayer(
  order: readonly PlayerId[], players: Readonly<Record<PlayerId, PlayerState>>,
  from: PlayerId,
): { readonly player: PlayerId; readonly wrapped: boolean } {
  const n = order.length;
  if (n === 0) return { player: from, wrapped: true };
  let at = order.indexOf(from);
  if (at < 0) at = 0;
  for (let step = 1; step <= n; step++) {
    const i = (at + step) % n;
    const id = order[i]!;
    const p = players[id];
    if (p === undefined || p.defeated || p.kind === "none") continue;
    return { player: id, wrapped: i <= at };
  }
  return { player: from, wrapped: true };
}

export function endTurn(c: Ctx, playerId: PlayerId): void {
  c.events.push({ type: "TurnEnded", player: playerId });

  // Who is alive is settled *before* the turn is handed on, because the day
  // boundary is where a grace period runs out: handing the turn to a player the
  // same boundary just eliminated parks the game on a kingdom that no longer
  // exists, and nothing but that player can end his own turn.
  if (nextPlayer(c.s.turn.order, c.s.players, playerId).wrapped) {
    advanceDay(c);
    evaluateVictory(c);
  }
  const { player } = nextPlayer(c.s.turn.order, c.s.players, playerId);
  patchTurn(c, { activePlayer: player });

  recomputeVisibility(c, player);
  c.events.push({
    type: "TurnStarted", player,
    day: c.s.turn.day, week: c.s.turn.week, month: c.s.turn.month,
  });
  evaluateVictory(c);
}

// ------------------------------------------------------------------- day

export function advanceDay(c: Ctx): void {
  let { day, week, month } = c.s.turn;
  day += 1;
  if (day > DAYS_PER_WEEK) {
    day = 1;
    week += 1;
    if (week > WEEKS_PER_MONTH) { week = 1; month += 1; }
  }
  patchTurn(c, { day, week, month });

  if (day === 1) startOfWeek(c);
  clearDailyTownFlags(c);
  dailyIncome(c);
  runAutoBuild(c);
  dailyRestore(c);
  // After the week's growth and before anybody moves: a column that was due
  // today walks in this morning, and it walks into the town as it stands NOW -
  // so a hall that changed hands overnight has already changed hands when its
  // reinforcements arrive, and they are lost rather than delivered to the man
  // who took it.
  deliverCaravans(c);
  tickTownlessGrace(c);
  warnStalemate(c);
}

// ------------------------------------------------------------------ week

function startOfWeek(c: Ctx): void {
  const monthStart = c.s.turn.week === 1;
  const ev = monthStart ? rollMonthEvent(c) : rollWeekEvent(c);

  if (monthStart) {
    c.events.push({ type: "MonthStarted", month: c.s.turn.month, event: ev.name });
  }
  c.events.push({ type: "WeekStarted", week: c.s.turn.week, event: ev.name });

  if (ev.plague) for (const id of entityKeys(c.s.towns)) halveAvailable(c, id as TownId);
  for (const id of entityKeys(c.s.towns)) applyWeeklyGrowth(c, id as TownId, ev.growth);

  weeklyObjectReset(c);
  for (const id of entityKeys(c.s.players)) {
    const p = c.s.players[id]!;
    if (p.defeated || p.kind === "none") continue;
    refreshTavern(c, id as PlayerId);
  }
}

interface WeekRoll {
  readonly name: string | null;
  readonly growth: GrowthEvent;
  readonly plague: boolean;
}

/** A creature drawn for a special week: any creature the content knows. */
function randomCreature(c: Ctx): CreatureDefId | null {
  const ids = [...c.content.creatures.keys()].sort();
  if (ids.length === 0) return null;
  return ids[rollBounded(c, "town.growth", ids.length)] ?? null;
}

function rollWeekEvent(c: Ctx): WeekRoll {
  if (!rollChanceBp(c, "town.growth", WEEK_OF_CREATURE_BP)) {
    return { name: null, growth: PLAIN_WEEK, plague: false };
  }
  const creature = randomCreature(c);
  if (creature === null) return { name: null, growth: PLAIN_WEEK, plague: false };
  return {
    name: `week.creature:${creature}`,
    growth: {
      allMulFp: FP_ONE, creature,
      creatureMulFp: FP_ONE, creatureFlat: WEEK_CREATURE_BONUS,
    },
    plague: false,
  };
}

function rollMonthEvent(c: Ctx): WeekRoll {
  // One roll decides between plague, a creature month and an ordinary month, so
  // the two cannot both fire in the same month.
  const r = rollBounded(c, "town.growth", 10000);
  if (r < MONTH_OF_PLAGUE_BP) {
    return { name: "month.plague", growth: PLAIN_WEEK, plague: true };
  }
  if (r < MONTH_OF_PLAGUE_BP + MONTH_OF_CREATURE_BP) {
    const creature = randomCreature(c);
    if (creature !== null) {
      return {
        name: `month.creature:${creature}`,
        growth: {
          allMulFp: FP_ONE, creature,
          creatureMulFp: MONTH_CREATURE_MUL_FP, creatureFlat: 0,
        },
        plague: false,
      };
    }
  }
  return { name: null, growth: PLAIN_WEEK, plague: false };
}

// ---------------------------------------------------------------- income

export function explainPlayerIncome(c: Ctx, p: PlayerId, r: ResourceKind): number {
  let total = 0;
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== p) continue;
    total += townIncome(c, t, r);
  }
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.kind !== "mine" || o.removed || o.owner !== p) continue;
    const def = c.content.mapObjects.get(o.def);
    const pay = { ...(def?.payload ?? {}), ...o.payload };
    if (pay["resource"] !== r) continue;
    const amt = pay["amount"];
    if (typeof amt === "number") total += amt;
  }
  total += heroIncome(c, p, r);
  return total;
}

/**
 * Hero-carried income is not only gold: a resource specialty and the class
 * passives put `income.mercury` and friends on a hero, and for a while the
 * daily pass read gold alone - so the Alchemist's mercury was authored,
 * tested for presence, shown in the debug panel, and never paid.
 */
function heroIncome(c: Ctx, p: PlayerId, r: ResourceKind): number {
  let total = 0;
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.owner !== p) continue;
    total += statFor(c, h, INCOME_STAT[r], 0).value;
  }
  return total;
}

function dailyIncome(c: Ctx): void {
  const pressure = stalematePressure(c);
  for (const pid of entityKeys(c.s.players)) {
    const p = c.s.players[pid]!;
    if (p.defeated || p.kind === "none") continue;
    for (const id of entityKeys(c.s.towns)) {
      const t = c.s.towns[id]!;
      if (t.owner !== pid) continue;
      for (const r of RESOURCES) {
        const v = townIncome(c, t, r);
        if (v > 0) addResource(c, pid as PlayerId, r, v);
      }
    }
    // Stewardship, its artifact equivalents, resource specialties and class
    // passives ride on the hero, not the town.
    for (const r of RESOURCES) {
      const v = heroIncome(c, pid as PlayerId, r);
      if (v > 0) addResource(c, pid as PlayerId, r, v);
    }
    // Garrison upkeep once the adjudication clock runs (see
    // `stalematePressure`). Zero before the warning day, so the opening pays
    // nothing and no early decision can move on account of it.
    if (pressure > 0) chargeGarrisonUpkeep(c, pid as PlayerId, pressure);
  }
  collectMineIncome(c);
  // Timed events fire at the day boundary beside the mines, because both are
  // the same thing: something the map pays out once a day without anybody
  // walking on it. A map with no event object does no work here.
  applyTimedEvents(c, absoluteDay(c.s as GameState));
}

// --------------------------------------------------------------- restores

function clearDailyTownFlags(c: Ctx): void {
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.builtThisTurn) patchTown(c, id as TownId, { builtThisTurn: false });
    if (t.researchedThisTurn === true) {
      patchTown(c, id as TownId, { researchedThisTurn: false });
    }
  }
}

const AUTO_SLOT_PRIORITY: Readonly<Record<string, number>> = Object.freeze({
  hall: 10_000_000,
  fort: 6_000_000,
  dwelling_7: 4_000_000,
  dwelling_6: 4_000_000,
  dwelling_5: 4_000_000,
  dwelling_4: 4_000_000,
  dwelling_3: 4_000_000,
  dwelling_2: 4_000_000,
  dwelling_1: 4_000_000,
  mage_guild: 3_000_000,
  marketplace: 2_800_000,
  resource_silo: 2_500_000,
  blacksmith: 1_600_000,
  tavern: 1_500_000,
});
const AUTO_SLOT_DEFAULT = 1_000_000;
/**
 * A fresh dwelling outranks everything, including the hall and fort chains:
 * rushing tier 3/4 in week one beats an income step or a wall step, and it
 * always beats polishing a dwelling the town already has. Upgrades keep
 * their plain slot weight, so they wait until no new dwelling is raisable.
 */
const AUTO_NEW_DWELLING_BONUS = 8_000_000;

function pickAutoBuildTarget(c: Ctx, t: TownState): BuildingDef | null {
  // One entry per slot: the town screen's `ResolvedSlot.next`, i.e. the base
  // when the slot is empty and the one step above what stands otherwise. A
  // second copy in a slot is never a "new structure" - `addBuilding` drops
  // the step an upgrade replaces - so a slot whose next step fails its own
  // prerequisites (e.g. an older dwelling step sitting under a freshly built
  // upgrade) offers nothing, the same as the screen offering no build button
  // there. `buildIllegality` answers this and affordability in one place.
  const bySlot = new Map<string, BuildingDef[]>();
  for (const b of c.content.buildings.values()) {
    if ((b.faction as string) !== (t.faction as string)) continue;
    const list = bySlot.get(b.slot);
    if (list === undefined) bySlot.set(b.slot, [b]);
    else list.push(b);
  }
  const depthOf = (id: BuildingDefId, guard = 16): number => {
    let depth = 0;
    let cur: BuildingDefId | null = id;
    while (cur !== null && depth < guard) {
      const def = c.content.buildings.get(cur);
      if (def === undefined) break;
      cur = def.upgradeOf;
      if (cur !== null) depth++;
    }
    return depth;
  };
  const built = new Set<string>(t.built as readonly string[]);
  const candidates: BuildingDef[] = [];
  for (const chain of bySlot.values()) {
    let top: BuildingDef | null = null;
    let topDepth = -1;
    for (const def of chain) {
      if (!built.has(def.id as string)) continue;
      const d = depthOf(def.id);
      if (d > topDepth) { top = def; topDepth = d; }
    }
    let next: BuildingDef | null = null;
    for (const def of [...chain].sort((a, b) =>
      depthOf(a.id) - depthOf(b.id) || ((a.id as string) < (b.id as string) ? -1 : 1))) {
      if (built.has(def.id as string)) continue;
      if (top === null) { if (def.upgradeOf === null) { next = def; break; } continue; }
      if (def.upgradeOf === top.id) { next = def; break; }
    }
    if (next === null) continue;
    if (buildIllegality(c, t, next.id) !== null) continue;
    candidates.push(next);
  }
  let best: BuildingDef | null = null;
  let bestScore = -Infinity;
  for (const b of candidates) {
    const base = AUTO_SLOT_PRIORITY[b.slot] ?? AUTO_SLOT_DEFAULT;
    let score = base;
    // A new creature structure beats an upgrade to one the town already has.
    if (b.dwellingFor !== null && b.upgradeOf === null) score += AUTO_NEW_DWELLING_BONUS;
    if (b.dwellingFor !== null) {
      const def = c.content.creatures.get(b.dwellingFor);
      if (def !== undefined) score += def.aiValue * (def.growth > 0 ? def.growth : 1);
    }
    if (b.guildLevel > 0) score += b.guildLevel * 1000;
    const gold = (b.cost as Record<string, number>)["gold"] ?? 0;
    score -= Math.floor(gold / 100);
    if (score > bestScore) { bestScore = score; best = b; }
  }
  return best;
}

function runAutoBuild(c: Ctx): void {
  for (const id of [...entityKeys(c.s.towns)].sort()) {
    const townId = id as TownId;
    const t = c.s.towns[townId]!;
    if (!t.autoBuild || t.owner === null || t.builtThisTurn) continue;
    const choice = pickAutoBuildTarget(c, t);
    if (choice === null) continue;
    const why = buildIllegality(c, t, choice.id);
    if (why !== null) continue;
    for (const rk of RESOURCES) {
      const v = (choice.cost as Record<string, number>)[rk] ?? 0;
      if (v > 0) addResource(c, t.owner, rk, -v);
    }
    if (choice.slot === GRAIL_SLOT) {
      const bearer = grailBearerIn(c, t);
      if (bearer !== null) spendGrail(c, bearer);
    }
    addBuilding(c, townId, choice);
    if (choice.guildLevel > 0) fillGuild(c, townId);
    const visiting = c.s.towns[townId]!.visitingHero;
    if (visiting !== null) arriveInTown(c, visiting, townId);
  }
}

function dailyRestore(c: Ctx): void {
  for (const id of entityKeys(c.s.heroes)) {
    const hid = id as HeroId;
    const before = c.s.heroes[hid]!;
    // Adventure-map buffs tick down once per day, not once per player turn.
    const mods = expireModifiers(before.modifiers, "turns");
    if (mods.length !== before.modifiers.length) patchHero(c, hid, { modifiers: mods });

    const h = c.s.heroes[hid]!;
    const move = dailyMovement(c, h);
    const cap = maxMana(c, h);
    // A hero resting in a town with a Mage Guild wakes with a full pool.
    const inGuildTown = h.inTown !== null && guildLevelOf(c, c.s.towns[h.inTown]!) > 0;
    const mana = inGuildTown ? cap : Math.min(cap, h.mana + manaRegen(c, h));
    patchHero(c, hid, { movementLeft: move, mana });
    c.events.push({ type: "HeroMovementSpent", hero: hid, left: move });
  }
}

// ------------------------------------------------------------ elimination

/**
 * The classic's grace period: a player who loses his last town has seven days to take
 * another one, and is out on the eighth.
 *
 * Without it the only loss rule is "no towns and no heroes", and a beaten player
 * who still has one homeless hero keeps the war running for ever - two AIs
 * played 120 days past a captured capital and `decided 0 of 2` because of
 * exactly this. The grace period is what turns "he has nothing left" into an
 * event with a date on it.
 */
export const TOWNLESS_GRACE_DAYS = 7;

/**
 * The countdown, advanced once per day for every player still in the game.
 *
 * It runs at the day boundary rather than at each player's own turn so that all
 * players spend the same seven days: a countdown ticked per turn would give the
 * player who moves first a longer reprieve than the one who moves last, and the
 * number of days between losing a town and losing the game would depend on the
 * turn order.
 */
function tickTownlessGrace(c: Ctx): void {
  for (const id of entityKeys(c.s.players)) {
    const pid = id as PlayerId;
    const p = c.s.players[pid]!;
    if (p.defeated || p.kind === "none") continue;
    const next = p.towns.length > 0 ? 0 : p.townlessDays + 1;
    if (next !== p.townlessDays) patchPlayer(c, pid, { townlessDays: next });
  }
}

/**
 * Take a player out of the game: his heroes leave the board with him, so that
 * nothing still standing on the map belongs to somebody who is no longer
 * playing. An eliminated player's heroes are *not* preserved into a tavern pool
 * the way a beaten hero is - there is no tavern left to hire them back from.
 *
 * This deliberately does not call `retireHero`: `execute.ts` imports this
 * module, so reaching back into it would make a cycle between two rule modules,
 * and a cycle makes resolution order unprovable. The town sweep below is
 * therefore written as "drop every dangling reference" rather than as "unhook
 * this hero from the town he says he is in", which is also the form that
 * survives a hero standing in somebody else's town.
 */
function eliminate(c: Ctx, pid: PlayerId, why: string): void {
  const p = c.s.players[pid]!;
  for (const hid of [...p.heroes].sort()) {
    if (c.s.heroes[hid] === undefined) continue;
    dropHero(c, hid);
    c.events.push({ type: "HeroDefeated", hero: hid, player: pid });
  }
  for (const id of entityKeys(c.s.towns)) {
    const tid = id as TownId;
    const t = c.s.towns[tid]!;
    const visiting = t.visitingHero !== null && c.s.heroes[t.visitingHero] === undefined
      ? null : t.visitingHero;
    const garrison = t.garrisonHero !== null && c.s.heroes[t.garrisonHero] === undefined
      ? null : t.garrisonHero;
    if (visiting !== t.visitingHero || garrison !== t.garrisonHero) {
      patchTown(c, tid, { visitingHero: visiting, garrisonHero: garrison });
    }
  }
  patchPlayer(c, pid, { defeated: true, heroes: [] });
  c.events.push({ type: "Message", player: pid, text: `eliminated:${why}` });
}

// --------------------------------------------------------------- scoring

/**
 * A kingdom's strength as one integer.
 *
 * Two things need it and neither can afford to disagree with the other: the
 * stalemate breaker below, which has to name a winner among kingdoms nobody
 * managed to beat, and the score a finished game is filed under. It is
 * deliberately coarse - a town outweighs any army that could plausibly take it
 * in a day - because its job is to separate two positions that a hundred days
 * of war failed to separate, not to price a trade.
 *
 * No rule reads it except the breaker. A player is never eliminated, refused or
 * charged anything on account of his score.
 */
export const SCORE_PER_TOWN = 1000;
export const SCORE_PER_BUILDING = 120;
export const SCORE_PER_MINE = 300;
export const SCORE_PER_HERO = 200;
export const SCORE_PER_HERO_LEVEL = 60;
/**
 * `aiValue` is priced in gold, so a stack of seventh-tier creatures would
 * outweigh a whole town several times over at face value. A tenth of it puts a
 * full army somewhere near the town it is standing in.
 */
export const SCORE_ARMY_DIVISOR = 10;
export const SCORE_GOLD_DIVISOR = 100;
/** A non-gold resource trades for a few hundred gold, so it is worth a town's fraction. */
export const SCORE_PER_RESOURCE = 8;

function armyValueOf(c: Ctx, id: ArmyId | null): number {
  if (id === null) return 0;
  const a = c.s.armies[id];
  if (a === undefined) return 0;
  let total = 0;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    const def = c.content.creatures.get(s.def);
    if (def === undefined) continue;
    total += def.aiValue * s.count;
  }
  return total;
}

export function kingdomScore(c: Ctx, pid: PlayerId): number {
  const p = c.s.players[pid];
  if (p === undefined) return 0;

  let flat = 0;
  let armyValue = 0;

  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== pid) continue;
    flat += SCORE_PER_TOWN + SCORE_PER_BUILDING * t.built.length;
    armyValue += armyValueOf(c, t.garrison);
  }
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.kind === "mine" && !o.removed && o.owner === pid) flat += SCORE_PER_MINE;
  }
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.owner !== pid) continue;
    flat += SCORE_PER_HERO + SCORE_PER_HERO_LEVEL * h.level;
    armyValue += armyValueOf(c, h.army);
  }
  for (const r of RESOURCES) {
    if (r === "gold") continue;
    flat += SCORE_PER_RESOURCE * p.resources[r];
  }

  // Two divisions, each rounded where it is taken. Carrying the whole score in
  // hundredths so that one rounding could be done at the end would be the
  // letter of the rule, and nothing downstream is precise enough to tell the
  // difference between the two answers.
  return flat
    + roundHalfAway(armyValue / SCORE_ARMY_DIVISOR)
    + roundHalfAway(p.resources.gold / SCORE_GOLD_DIVISOR);
}

/**
 * The player with the highest score among those still standing.
 *
 * The scan follows `turn.order` rather than the `alive` array so that a tie is
 * broken by the seating, which is the one ordering a player can see; iterating
 * `alive` would break it by whatever order the caller happened to build.
 */
export function scoreLeader(c: Ctx, alive: readonly PlayerId[]): PlayerId | null {
  let best: PlayerId | null = null;
  let bestScore = -1;
  for (const pid of c.s.turn.order) {
    if (!alive.includes(pid)) continue;
    const s = kingdomScore(c, pid);
    if (s > bestScore) { best = pid; bestScore = s; }
  }
  return best;
}

// ------------------------------------------------------------- stalemate

/**
 * Two kingdoms that cannot finish each other off used to circle until the
 * harness stopped counting - three of twenty four-player games ended that way,
 * with nobody eliminated and no condition met. The classic has no rule for this
 * because the classic has a human who concedes.
 *
 * The breaker is a clock, not a nerf. Nothing about the game changes on the
 * warning day or on any day between; on the deciding day the kingdom that is
 * ahead on `kingdomScore` is awarded the game, the way an adjudicated board
 * game is. Deciding it on the board rather than on a coin is what keeps the
 * hundred days before it from having been wasted.
 */
export const STALEMATE_WARNING_DAY = 150;
export const STALEMATE_DECIDE_DAY = 200;

/**
 * The absolute day this game will be adjudicated on.
 *
 * A scenario whose own condition is a clock set *past* the breaker would be cut
 * off before it could be won, so the breaker steps aside for it: a map that
 * says "hold out for 250 days" gets its 250 days and is adjudicated the day
 * after, if holding out did not produce a single survivor.
 */
export function stalemateDay(c: Ctx): number {
  const { kind, arg } = parseVictoryCondition(c.s.victory.condition);
  if (kind !== "survive" && kind !== "survive_until_day") return STALEMATE_DECIDE_DAY;
  const n = Number.parseInt(arg, 10);
  if (!Number.isFinite(n) || n < STALEMATE_DECIDE_DAY) return STALEMATE_DECIDE_DAY;
  return n + 1;
}

/**
 * How far the adjudication clock has run, as a fraction from 0 to 1.
 *
 * Zero on and before the warning day, one on and after the deciding day, and
 * linear in between. The breaker below still decides on the day alone - this
 * is purely the pressure the endgame rules (garrison upkeep here, the AI's
 * conquest escalation and Grail chase on its side) read so that long turtling
 * games resolve on the board instead of circling. A `survive` scenario's
 * shifted window moves both ends with it, exactly like the warning does.
 *
 * The AI imports this rather than recomputing it, so the two clocks cannot
 * drift apart.
 */
export function stalematePressure(c: Ctx): number {
  const decide = stalemateDay(c);
  const span = STALEMATE_DECIDE_DAY - STALEMATE_WARNING_DAY;
  const warn = decide - span;
  const day = absoluteDay(c.s as GameState);
  if (day <= warn) return 0;
  if (day >= decide) return 1;
  return (day - warn) / span;
}

/**
 * The `aiValue` of every army this player owns, garrisons and field forces
 * alike. Shared by the score and the upkeep below so the two cannot price an
 * army differently.
 */
function kingdomArmyValue(c: Ctx, pid: PlayerId): number {
  let total = 0;
  for (const id of entityKeys(c.s.armies)) {
    const a = c.s.armies[id]!;
    if (a.owner !== pid) continue;
    for (const s of a.slots) {
      if (s === null || s.count <= 0) continue;
      const def = c.content.creatures.get(s.def);
      if (def === undefined) continue;
      total += def.aiValue * s.count;
    }
  }
  return total;
}

/**
 * What hoarding an army away from the deciding day costs, in gold per day.
 *
 * A kingdom sitting on three million points of troops in its halls is ahead on
 * `kingdomScore` by three hundred thousand - unassailable - while never
 * fielding a stack. The charge prices the hoard at a small fraction of its
 * `aiValue` (basis points, so it reads as upkeep rather than a fine) scaled by
 * `stalematePressure`, which is what bleeds the scoreboard leader exactly when
 * the breaker approaches: gold spent is score foregone, while an army that
 * marches and dies costs its owner nothing more.
 *
 * Floored at zero gold rather than driven negative: a kingdom that cannot pay
 * loses its treasury, not its troops. Small enough to be noise against real
 * income until pressure is high - a 3M hoard at full pressure pays 3000 a day,
 * about a Capitol - and zero before the warning day, so the opening and the
 * first hundred days never see it.
 */
export const GARRISON_UPKEEP_BP = 10;

function chargeGarrisonUpkeep(c: Ctx, pid: PlayerId, pressure: number): void {
  const held = kingdomArmyValue(c, pid);
  if (held <= 0) return;
  const due = Math.floor((held * GARRISON_UPKEEP_BP * pressure) / 10000);
  if (due <= 0) return;
  addResource(c, pid, "gold", -due);
}

/**
 * The warning, emitted once on the day it becomes true.
 *
 * It lives on the day boundary rather than in `evaluateVictory` because that
 * function runs several times a turn: a warning repeated forty times is not a
 * warning, and there is no room in state to remember having given it.
 */
function warnStalemate(c: Ctx): void {
  const decide = stalemateDay(c);
  const warn = decide - (STALEMATE_DECIDE_DAY - STALEMATE_WARNING_DAY);
  if (absoluteDay(c.s as GameState) !== warn) return;
  c.events.push({
    type: "Message", player: null, text: `stalemate.warning:${String(decide)}`,
  });
}

// --------------------------------------------------------------- victory

/**
 * Two loss rules, and the second is the one that makes a game end.
 *
 * A player with neither town nor hero is out at once - there is nothing left to
 * play with. A player who still has a hero but no town is on the clock: seven
 * days to take one, out on the eighth. Taking a town clears the clock, and it
 * is cleared here rather than at the capture site so that every route into
 * owning a town - a siege, an undefended walk-in, a scenario gift - clears it
 * by the same rule and none of them can forget to.
 */
export function evaluateVictory(c: Ctx): void {
  if (c.s.victory.over) return;
  // Anything raised on the winning command is closed with the game below, at
  // the two points that set `over`. `gate` refuses every command once the game
  // is decided, RESPOND_TO_INTERACTION included, so a prompt left open by the
  // blow that ends the war is a modal dialog whose only button is refused for
  // ever - and, in the shell, one that covers the end screen it is standing on
  // top of. The last hero to level up on the last battle costs nothing; being
  // unable to read that you won costs the whole game.

  for (const id of entityKeys(c.s.players)) {
    const pid = id as PlayerId;
    const p = c.s.players[pid]!;
    if (p.defeated || p.kind === "none") continue;
    if (p.towns.length > 0) {
      if (p.townlessDays !== 0) patchPlayer(c, pid, { townlessDays: 0 });
      continue;
    }
    if (p.heroes.length === 0) { eliminate(c, pid, "no_town_no_hero"); continue; }
    if (p.townlessDays > TOWNLESS_GRACE_DAYS) eliminate(c, pid, "grace_expired");
  }

  const alive: PlayerId[] = [];
  for (const id of entityKeys(c.s.players)) {
    const p = c.s.players[id]!;
    if (!p.defeated && p.kind !== "none") alive.push(id as PlayerId);
  }

  const winner = conditionWinner(c, alive);
  if (winner !== null) { declareWinner(c, winner); return; }
  // A one-player scenario is a sandbox, not a game already won.
  if (alive.length <= 1 && c.s.turn.order.length > 1) {
    declareWinner(c, alive[0] ?? null);
    return;
  }

  // Last, because an elimination or a met condition is a game somebody won and
  // this is only a game nobody lost.
  if (alive.length > 1 && absoluteDay(c.s as GameState) >= stalemateDay(c)) {
    const w = scoreLeader(c, alive);
    c.events.push({
      type: "Message", player: null,
      text: `stalemate.decided:${String(absoluteDay(c.s as GameState))}`,
    });
    declareWinner(c, w);
  }
}

/** The game is over and this is who won; closes any prompt the last blow left open. */
export function declareWinner(c: Ctx, winner: PlayerId | null): void {
  if (c.s.victory.over) return;
  c.s.victory = { condition: c.s.victory.condition, winner, over: true };
  c.events.push({ type: "GameOver", winner });
  closePending(c);
}

/**
 * The scenario condition, encoded as `"<kind>:<argument>"` so a map file can
 * carry it as one string. `defeat_all` is the default and is decided by the
 * survivor count rather than here.
 */
function conditionWinner(c: Ctx, alive: readonly PlayerId[]): PlayerId | null {
  const cond = c.s.victory.condition;
  const sep = cond.indexOf(":");
  const kind = sep < 0 ? cond : cond.slice(0, sep);
  const arg = sep < 0 ? "" : cond.slice(sep + 1);

  switch (kind) {
    case "capture_town": {
      const t = c.s.towns[arg as TownId];
      if (t === undefined || t.owner === null) return null;
      return alive.includes(t.owner) ? t.owner : null;
    }
    case "defeat_hero": {
      if (c.s.heroes[arg as HeroId] !== undefined) return null;
      return alive.length === 1 ? alive[0]! : null;
    }
    case "acquire_artifact": {
      for (const id of entityKeys(c.s.heroes)) {
        const h = c.s.heroes[id]!;
        if (h.backpack.includes(arg as never)) return h.owner;
        for (const k of Object.keys(h.artifacts).sort()) {
          if (h.artifacts[k] === (arg as never)) return h.owner;
        }
      }
      return null;
    }
    case "accumulate_gold": {
      const need = Number.parseInt(arg, 10);
      if (!Number.isFinite(need)) return null;
      for (const id of alive) {
        const p = c.s.players[id]!;
        if (p.resources.gold >= need) return id;
      }
      return null;
    }
    case "survive": {
      const days = Number.parseInt(arg, 10);
      if (!Number.isFinite(days)) return null;
      if (absoluteDay(c.s as GameState) < days) return null;
      return alive.length === 1 ? alive[0]! : null;
    }
    case "survive_until_day": {
      // The difference from `survive` is the whole point of the kind: `survive`
      // asks to be the last one standing and merely refuses to end before day
      // N, so a map set to it can still run for ever. This one ends on day N
      // whoever is left, and the board breaks the tie. It is the only condition
      // that can finish a game nobody is winning.
      const days = Number.parseInt(arg, 10);
      if (!Number.isFinite(days)) return null;
      if (absoluteDay(c.s as GameState) < days) return null;
      return scoreLeader(c, alive);
    }
    case "flag_all_mines": {
      // Every mine on the map, not every mine the player has seen: an unowned
      // mine in unexplored fog is exactly the one that should keep this false.
      let owner: PlayerId | null = null;
      for (const id of entityKeys(c.s.objects)) {
        const o = c.s.objects[id]!;
        if (o.kind !== "mine" || o.removed) continue;
        if (o.owner === null) return null;
        if (owner === null) owner = o.owner;
        else if (owner !== o.owner) return null;
      }
      // A map with no mine left is nobody's win rather than everybody's; the
      // gate in `resolveVictoryCondition` refuses such a map up front, and this
      // is the same answer for a map whose last mine was removed mid-game.
      if (owner === null) return null;
      return alive.includes(owner) ? owner : null;
    }
    case "build_grail": {
      // The slot, not an id: the Grail is one building per faction and they all
      // fill the same slot (`GRAIL_SLOT`, create.ts).
      for (const id of entityKeys(c.s.towns)) {
        const t = c.s.towns[id]!;
        if (t.owner === null || !alive.includes(t.owner)) continue;
        for (const b of t.built) {
          if (c.content.buildings.get(b)?.slot === GRAIL_SLOT) return t.owner;
        }
      }
      return null;
    }
    default:
      return null;
  }
}
